/**
 * OnboardingChecklist — persistent Getting-Started widget on the V3 dashboard.
 *
 * Renders as a compact card while the user is still activating. Each
 * task auto-detects completion server-side from real activity in the
 * workspace, then celebrates + goes away when 100% complete (or when
 * the user explicitly dismisses it).
 */
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import {
  CheckCircle2, Circle, X, ArrowRight, Sparkles, PartyPopper,
} from "lucide-react";

export default function OnboardingChecklist() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dismissing, setDismissing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/v3/onboarding/progress");
      setData(data);
    } catch { setData(null); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const dismiss = useCallback(async () => {
    if (dismissing) return;
    setDismissing(true);
    try { await api.post("/v3/onboarding/checklist-hidden"); }
    catch { /* silent */ }
    finally { setData((d) => (d ? { ...d, checklist_hidden: true } : d)); }
  }, [dismissing]);

  if (loading || !data) return null;
  if (data.checklist_hidden) return null;

  const visibleTasks = (data.tasks || []).filter((t) => !t.dismissed);
  if (visibleTasks.length === 0) return null;
  const pct = data.percent ?? 0;

  return (
    <div
      className="rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 via-white to-white p-5 shadow-sm"
      data-testid="v3-onboarding-checklist"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-1.5 rounded-full text-[10px] font-black tracking-widest uppercase px-2.5 py-1 text-white"
                style={{ backgroundImage: "linear-gradient(90deg, #F59E0B, #EA580C)" }}>
            <Sparkles className="w-3 h-3" /> Getting Started
          </span>
          <span className="text-[11px] font-bold uppercase tracking-widest text-slate-500 tabular-nums">
            {data.completed} / {data.total} · {pct}%
          </span>
        </div>
        <button type="button" onClick={dismiss}
                data-testid="v3-onboarding-dismiss"
                aria-label="Dismiss checklist"
                className="rounded-full hover:bg-slate-100 p-1.5">
          <X className="w-3.5 h-3.5 text-slate-400" />
        </button>
      </div>

      {/* Progress bar */}
      <div className="mt-3 h-1.5 w-full rounded-full bg-slate-100 overflow-hidden">
        <div className="h-full rounded-full transition-[width] duration-700"
             style={{ width: `${pct}%`,
                       backgroundImage: "linear-gradient(90deg, #6366F1, #F59E0B, #8B5CF6)" }} />
      </div>

      {data.all_done ? (
        <div className="mt-4 flex items-start gap-3 rounded-xl bg-emerald-50 border border-emerald-200 p-4">
          <PartyPopper className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
          <div>
            <div className="text-sm font-bold text-slate-900">You're fully activated 🎉</div>
            <p className="mt-1 text-[12.5px] leading-relaxed text-slate-600">
              You've hit every core loop. Time to compound — bring in a JV partner,
              share a report, or run a bigger SMS blast.
            </p>
          </div>
        </div>
      ) : (
        <ul className="mt-4 space-y-2">
          {visibleTasks.map((t) => (
            <ChecklistRow key={t.key} task={t} />
          ))}
        </ul>
      )}

      <p className="mt-3 text-[11px] text-slate-400">
        This card disappears once you're activated — or you can dismiss it above.
      </p>
    </div>
  );
}

function ChecklistRow({ task }) {
  const Icon = task.done ? CheckCircle2 : Circle;
  return (
    <li className={`group rounded-xl border ${task.done ? "border-emerald-200 bg-emerald-50/40" : "border-slate-200 bg-white"} px-3 py-2.5 flex items-center gap-3`}
        data-testid={`v3-onboarding-task-${task.key}`}>
      <Icon className={`w-4 h-4 shrink-0 ${task.done ? "text-emerald-500" : "text-slate-300 group-hover:text-slate-400"}`} />
      <div className="flex-1 min-w-0">
        <div className={`text-[13px] font-semibold leading-tight ${task.done ? "text-slate-500 line-through" : "text-slate-900"}`}>
          {task.title}
        </div>
        <div className="text-[11.5px] text-slate-500 leading-snug">{task.hint}</div>
      </div>
      {!task.done && task.cta_href && (
        <Link to={task.cta_href}
              data-testid={`v3-onboarding-task-${task.key}-cta`}
              className="shrink-0 inline-flex items-center gap-1 rounded-lg text-[11px] font-bold px-2.5 py-1.5 text-white shadow-sm"
              style={{ backgroundImage: "linear-gradient(135deg, #6366F1, #4F46E5)" }}>
          Do it <ArrowRight className="w-3 h-3" />
        </Link>
      )}
    </li>
  );
}
